/**
 * DEUX STYLES NOMMÉS — « ÉTAT » ET « QUALITÉ ».
 *
 * ⚠️⚠️ CE N'EST PAS UNE OPTION D'AFFICHAGE, C'EST LA CORRECTION D'UNE ERREUR
 * DE CONCEPTION. Le plan répondait à DEUX questions dans un seul style :
 * l'état juridique d'une parcelle (inscrite / en instruction / suspendue) et la
 * fiabilité de sa géométrie (relevée ou présumée, bâti rattaché ou non). D'où
 * la pénurie de fentes de teinte, et d'où le fait que « en instruction » se
 * soit retrouvé à 40° du bâti sur la roue des teintes : il ne restait plus de
 * place.
 *
 * ⚠️ LES CADASTRES QUI SAVENT FAIRE EN PUBLIENT DEUX. Le Kadaster néerlandais
 * sert le même jeu de données sous `standaardvisualisatie` et
 * `kwaliteitsvisualisatie` (relevé le 22/09/2026 sur
 * api.pdok.nl/kadaster/brk-kadastrale-kaart). Dans le premier, la limite porte
 * son statut juridique par la teinte, à épaisseur constante : définitive
 * #000001, provisoire #CC9900, administrative #5CF6D1, et le bâtiment en
 * #C10101. Dans le second, **le bâtiment se retire en gris** (#E6E6E6 avec un
 * contour #CDCDCD) parce qu'il n'est pas le sujet, et la limite prend une rampe
 * séquentielle à quatre crans. Deux questions, deux styles.
 *
 * ⚠️ ET LEUR RAMPE EST UN CHOIX QUE NOUS NE COPIONS PAS. La leur va du vert
 * #008200 à l'orange #fe9c33 — des valeurs ColorBrewer, donc validées, mais un
 * axe vert→orange s'effondre en deutéranopie. La nôtre est à **teinte unique
 * ordonnée par la clarté** : elle reste lisible en vision déficiente ET en
 * monochrome, par construction, ce qui est la règle posée au lot 1.
 */

/**
 * LES SIX VALEURS DE `cad.origine_donnee`, DE LA PREUVE LA PLUS FORTE À LA PLUS
 * FAIBLE. L'ordre n'est pas celui de l'enum : il est cartographique.
 */
export const ORDRE_ORIGINE = [
  'leve_terrain',        // relevé GNSS ou station par un géomètre agréé
  'numerisation_plan',   // repris d'un plan existant
  'import',              // versé depuis un autre registre
  'declaration',         // affirmé par une partie
  'derive_bati',         // déduit d'une emprise de bâtiment
  'maille_systematique', // carroyage — aucune preuve, un canevas
] as const;

export type Origine = (typeof ORDRE_ORIGINE)[number];

/**
 * LES QUATRE CLASSES DE PREUVE — LUES DANS LE DOMAINE, PAS DÉDUITES DU RENDU.
 *
 * ⚠️⚠️ CE GROUPEMENT EST UNE DÉCISION DE DROIT AVANT D'ÊTRE UNE DÉCISION DE
 * COULEUR, et le dépôt l'avait déjà tranchée — dans les commentaires de l'enum
 * lui-même (`002_cadastre_geometrie.sql`), et en trois endroits du domaine :
 *
 *   'derive_bati'   -- dérivée d'une emprise bâtie — INDICATIVE, jamais opposable
 *   'declaration'   -- déclarée par un usager, non contrôlée
 *   'leve_terrain'  -- relevé GNSS/station par un géomètre agréé
 *   « un levé rend une parcelle opposable »   (partage/gestes-metier.ts)
 *   « un levé contradictoire la rend opposable » (partage/echafaudage.ts)
 *   « Validé — la géométrie est opposable »   (partage/libelles.ts)
 *
 * L'axe du registre est donc **l'opposabilité**, et il est écrit. J'avais
 * d'abord inventé une échelle relevé/reporté/déclaré/présumé à partir du nombre
 * de crans que le fond blanc supporte : c'était prendre une contrainte de rendu
 * pour une règle de droit. Signalé par la session mowibo-ia-be, vérifié, corrigé.
 * Que le domaine donne quatre classes et que le blanc en porte quatre est une
 * COÏNCIDENCE heureuse — pas la cause.
 *
 *   opposable   le levé d'un géomètre agréé rend la parcelle opposable
 *   reporte     repris d'un autre document : l'opposabilité est empruntée,
 *               elle n'est pas établie ici
 *   declare     affirmé par une partie, « non contrôlée » — engage son auteur
 *               et personne d'autre
 *   indicative  « jamais opposable », mot de l'enum : rien ne se prouve avec
 *
 * ⚠️ UN SEUL POINT N'EST PAS TRANCHÉ PAR LE DOMAINE : `maille_systematique`,
 * ajoutée par un `ALTER TYPE` en migration 021, n'a **aucun commentaire**. La
 * ranger en `indicative` est ma lecture — un carroyage est au mieux indicatif,
 * a fortiori si une emprise bâtie l'est déjà. C'est la seule ligne de ce
 * tableau qui attende un arbitrage du fondateur, et elle porte 988 679 des
 * 988 695 « parcelles » du canevas.
 */
export const CLASSE_PREUVE = {
  leve_terrain: 'opposable',
  numerisation_plan: 'reporte',
  import: 'reporte',
  declaration: 'declare',
  derive_bati: 'indicative',
  /**
   * ⚠️⚠️ LA MAILLE N'EST PAS UN DEGRÉ DE PREUVE — ELLE N'AFFIRME RIEN.
   *
   * Je l'avais rangée en `indicative`, et c'était faux. Signalé par la session
   * mowibo-ia-be, remesuré : ces 988 688 lignes sont l'échafaudage 25 m × 25 m
   * de la couverture systématique (§5 ter, migrations 021-027), dont la seule
   * fonction est que toute parcelle réelle ait un contenant. Et elles sont
   * **toutes `retiree`** — `maille_systematique` vivante = 0, contre 1 761 150
   * pour `derive_bati` (témoin positif joué).
   *
   * Le classer `indicative` ne peignait donc RIEN aujourd'hui. Mais ça
   * inscrivait l'équivalence « échafaudage = indicative », qui deviendrait
   * fausse le jour où quelqu'un ressème des mailles : une réservation ne dit
   * pas « on sait un peu », elle dit « on ne sait rien, la place est tenue ».
   * Mettre un échafaudage sur une échelle de preuve, c'est lui prêter une
   * affirmation qu'il ne fait pas.
   *
   * ⚠️ IL RESTE UN CAS EXPLICITE DU `match`, et c'est indispensable : le sortir
   * du tableau le ferait peindre par le REPLI, donc par accident — le défaut
   * de `projet`, exactement.
   *
   * ⚠️⚠️ TRANCHÉ PAR LE FONDATEUR LE 22/09/2026 : **hors échelle**. La question
   * posée était « un échafaudage qui réserve une place doit-il porter une classe
   * de preuve ? » — réponse non. Ce n'est plus une lecture de ma part en attente
   * d'arbitrage : c'est une décision, et elle ne se réouvre pas sans lui.
   */
  maille_systematique: 'reserve',
} as const satisfies Record<Origine, string>;

export const ORDRE_PREUVE = ['opposable', 'reporte', 'declare', 'indicative'] as const;
export type ClassePreuve = (typeof ORDRE_PREUVE)[number];

/** Hors de l'échelle de preuve : ce qui ne prétend rien. */
export const HORS_PREUVE = ['reserve'] as const;
export type ClasseOrigine = ClassePreuve | (typeof HORS_PREUVE)[number];

/**
 * LA RÉSERVE — achromatique et TIRETÉE, et le tiret n'est pas un ornement.
 *
 * ⚠️ MESURÉ : en thème sombre, la réserve (#6c6c6c, L*=45,6) et le cran
 * `indicative` (L*=39,6) ne sont qu'à **6,0 L\*** l'un de l'autre — sous le
 * seuil de 8 où deux traits se séparent par la clarté seule. C'est donc le
 * MOTIF qui les distingue, et c'est la règle du lot 1 : deux classes se
 * séparent par leur motif OU par leur épaisseur, jamais par la seule teinte.
 * En clair l'écart suffirait (8,4), mais une règle qui ne vaut que dans un
 * thème n'est pas une règle.
 */
export const RESERVE: Readonly<Record<'clair' | 'sombre', { couleur: string; motif: readonly number[] }>> = {
  clair:  { couleur: '#7a7a7a', motif: [4, 2] },
  sombre: { couleur: '#6c6c6c', motif: [4, 2] },
};

export const LIBELLE_PREUVE: Readonly<Record<ClasseOrigine, string>> = {
  opposable: 'opposable — levé d’un géomètre agréé',
  reporte: 'reportée d’un autre document',
  declare: 'déclarée, non contrôlée',
  indicative: 'indicative — jamais opposable',
  reserve: 'réservation — n’affirme rien sur le sol',
};

/**
 * ⚠️ POURQUOI QUATRE CRANS ET PAS SIX, une fois le droit posé : le fond blanc
 * n'en porte pas plus. Mesuré le 22/09 — une rampe à teinte unique garde un
 * contraste ≥ 3 partout et un écart de clarté ≥ 8 L* entre voisins avec une
 * marge de **17,8 L* à quatre crans**, qui tombe à **10,5 à six crans**, le
 * dernier cran n'étant plus qu'à **3,10** de contraste. Ce chiffre vit ici et
 * non dans une passation : c'est lui qui doit arrêter la main de qui voudra
 * ajouter un cinquième cran « puisqu'il y a six valeurs d'enum ».
 */
export const RAMPE_PREUVE: Readonly<Record<'clair' | 'sombre', Record<ClassePreuve, string>>> = {
  clair:  { opposable: '#001534', reporte: '#193c67', declare: '#436795', indicative: '#6e95c6' },
  sombre: { opposable: '#bddaff', reporte: '#88b0e2', declare: '#6186b7', indicative: '#3b5f8d' },
};

/**
 * LE RETRAIT DU BÂTI DANS LA VUE QUALITÉ — la leçon directe du Kadaster.
 * Le bâtiment n'est pas le sujet de cette vue : il cesse d'être coloré et
 * devient un fond de repérage. Sans ce retrait, la masse du bâti (1 744 535
 * emprises) écrase une rampe qui ne porte que sur des limites.
 */
export const BATI_EFFACE: Readonly<Record<'clair' | 'sombre', { corps: string; trait: string }>> = {
  clair:  { corps: '#ececec', trait: '#c4c4c4' },
  sombre: { corps: '#1e1e1e', trait: '#333333' },
};

export const STYLES = ['etat', 'qualite'] as const;
export type StylePlan = (typeof STYLES)[number];

export const LIBELLE_STYLE: Readonly<Record<StylePlan, string>> = {
  etat: 'État juridique',
  qualite: 'Qualité du levé',
};

/**
 * ⚠️⚠️ LE GARDE DE POPULATION — REFUSER D'OFFRIR UNE VUE QUI NE DISTINGUE RIEN.
 *
 * Mesuré en base le 22/09/2026 : **1 761 150 parcelles sur 1 761 157 sont en
 * `derive_bati`, soit 99,9996 %**. La vue qualité peindrait donc une seule
 * couleur sur tout le pays. Elle serait VRAIE, et inutile — pire, elle
 * suggérerait qu'on a mesuré la qualité du parcellaire alors qu'on n'a mesuré
 * personne. Un contrôle qui ne discrimine rien est un contrôle qui ment.
 *
 * ⚠️ ET LE CAS « JE N'AI PAS SU MESURER » NE SE CONFOND PAS AVEC « TOUT EST
 * DANS UNE SEULE CLASSE ». Les deux rendraient « pas de basculeur », et le
 * lecteur en tirerait deux conclusions opposées. C'est la forme du témoin de
 * mowibo-ia-be : une tuile hors zone et une tuile vide rendent toutes deux
 * zéro objet. On distingue donc explicitement `inconnu` de `degenere`.
 */
export type Verdict =
  | { readonly offerte: true; readonly classes: number; readonly part: number }
  | {
      readonly offerte: false;
      readonly cause: 'inconnu' | 'vide' | 'degenere';
      /** Ce qui s'AFFICHE — court, pour ne pas recouvrir le reste du cockpit. */
      readonly court: string;
      /** Ce qui s'explique — dans l'infobulle, jamais dans le bandeau. */
      readonly motif: string;
    };

/** Au-delà de cette part dans une seule classe, la vue ne distingue plus rien. */
export const PART_DEGENERATIVE = 0.95;

/**
 * ⚠️ SEULES LES QUATRE CLASSES DE PREUVE COMPTENT. Une population entièrement
 * en `reserve` n'a rien à dire sur l'opposabilité : offrir la vue y montrerait
 * une seule couleur, ce qui est le cas dégénéré sous un autre nom.
 */
export function offreQualite(
  distribution: Readonly<Partial<Record<ClasseOrigine, number>>> | null | undefined,
): Verdict {
  if (distribution === null || distribution === undefined) {
    return { offerte: false, cause: 'inconnu',
             court: 'qualité du levé — répartition illisible',
             motif: 'la répartition des origines n’a pas pu être lue — ce n’est pas « tout est pareil »' };
  }
  const paires = ORDRE_PREUVE
    .map((c) => [c, distribution[c] ?? 0] as const)
    .filter(([, n]) => n > 0);
  const total = paires.reduce((s, [, n]) => s + n, 0);
  if (total === 0) {
    return { offerte: false, cause: 'vide',
             court: 'qualité du levé — registre vide',
             motif: 'aucune parcelle au registre pour ce territoire' };
  }
  const [classeDominante, nDominant] = paires.reduce((a, b) => (b[1] > a[1] ? b : a));
  const part = nDominant / total;
  if (part >= PART_DEGENERATIVE) {
    const pct = (part * 100).toFixed(part > 0.999 ? 4 : 1).replace('.', ',');
    /**
     * ⚠️ DEUX TEXTES, ET C'EST UNE CORRECTION D'ÉCRAN, PAS UNE COQUETTERIE.
     * Le motif complet faisait 118 caractères dans un jeton de cockpit : il
     * traversait toute la carte et recouvrait les coordonnées et les autres
     * avertissements — vu par le fondateur sur une capture. Un message qui
     * cache les autres ne prévient de rien, il efface.
     * `court` s'affiche, `motif` va dans l'infobulle : la raison reste
     * atteignable, elle cesse d'être encombrante.
     */
    return {
      offerte: false, cause: 'degenere',
      court: `qualité du levé — 1 classe à ${pct} %`,
      motif: `${pct} % des ${total.toLocaleString('fr-FR')} parcelles sont « ` +
             `${LIBELLE_PREUVE[classeDominante]} » — la vue qualité ne distinguerait rien`,
    };
  }
  return { offerte: true, classes: paires.length, part };
}

/**
 * La classe d'une origine — un degré de preuve, OU la réservation.
 *
 * ⚠️ LE TYPE DE RETOUR DIT LA FRONTIÈRE. Il rendait `ClassePreuve`, ce qui
 * obligeait à un `as` pour faire passer `reserve` : un cast qui musèle
 * exactement le contrôle qui aurait signalé qu'une réservation n'est pas un
 * degré de preuve. C'est le motif du `as TypeVehicule` qui a rendu deux gammes
 * incommandables. Le type porte donc les deux familles, explicitement.
 */
export function classeDe(origine: string): ClasseOrigine | undefined {
  return (CLASSE_PREUVE as Record<string, ClasseOrigine>)[origine];
}

/**
 * L'expression MapLibre qui peint une limite selon sa classe de preuve.
 *
 * ⚠️ TOUTES LES ORIGINES SONT ÉNUMÉRÉES EXPLICITEMENT, et le repli n'est là que
 * pour une valeur d'enum future. C'est la leçon de `projet` : une valeur absente
 * du `match` est peinte par le repli, donc par accident, et le rendu paraît
 * juste jusqu'au jour où il ne l'est plus. La garde compare les cas de cette
 * expression aux valeurs de l'enum lues dans les migrations.
 */
export function couleurPreuve(theme: 'clair' | 'sombre'): unknown[] {
  const rampe = RAMPE_PREUVE[theme];
  const expr: unknown[] = ['match', ['get', 'origine']];
  for (const origine of ORDRE_ORIGINE) {
    const classe = CLASSE_PREUVE[origine];
    expr.push(origine, classe === 'reserve' ? RESERVE[theme].couleur : rampe[classe]);
  }
  /* Repli : une origine inconnue est INDICATIVE, jamais « sûre » — et jamais
   * `reserve` non plus, qui affirmerait qu'on tient sciemment une place. */
  expr.push(rampe.indicative);
  return expr;
}
